import { Fragment, useEffect, useState } from 'react';
import Table from 'shell/components/shared/table';
import { generatePath, useNavigate } from 'react-router-dom';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValues } from '..';
import { useFormik } from 'formik';
import { transformDataset } from 'front-office/utils/release-mode-transform-dataset';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { headCells, validationSchemaFilter } from 'front-office/constants/release-mode-list';
import ListHeader from 'shell/components/shared/list-header';
import { HttpResponseList } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { authsObj } from 'front-office/constants/release-mode-authorizations';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ReleaseMode } from 'shell/models/release-mode';
import {
  ReleaseModeResponse,
  ReleaseModeClient,
  FiltersFormReleaseMode,
} from 'front-office/view-models/release-mode';
import { transformReleaseModeList } from 'front-office/components/release-mode/utils/transform-release-mode-list';
import { Typography, useEventCallback } from '@mui/material';
import { Order } from 'shell/view-models/order';
import { ReleaseModeCrumbs } from 'front-office/constants/release-mode-crumbs';
import FiltersReleaseMode from 'front-office/components/release-mode/list/filter';

const ReleaseModeList = (): React.ReactElement => {
  const [releaseModeList, getReleaseModeList] = useHttpFetch<
    HttpResponseList<ReleaseMode>,
    ReleaseModeResponse
  >(`${__CERTIFICATION_API_URL__}/certificazione/modalita-rilascio`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformReleaseModeList,
  });

  const deleteFeatureAuth = useHttpClient('', {
    options: {
      errorCatching: true,
      method: 'delete',
      loading: true,
      data: {},
    },
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<ReleaseModeClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<ReleaseModeClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormReleaseMode>();

  const onSubmit = useEventCallback((formValues: FiltersFormReleaseMode) => {
    const queryString = transformDataset({ ...formValues, page: 0 });
    filterForm.setFieldValue('page', 0);
    getReleaseModeList({
      params: {},
      paramsSerializer: () => queryString,
    });
  });

  const filterForm = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchemaFilter,
    onSubmit,
  });

  useEffect(() => {
    return () => filterTable.setValues(filterForm.values);
  }, [filterForm.values]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useEventCallback(
    (cell: ReleaseModeClient): Promise<ReleaseModeClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sportello/modalita-rilascio/dettaglio/:id', { id: cell.code || '' })
        );
        resolve(cell);
      });
    }
  );

  const onEditPress = useEventCallback((cell: ReleaseModeClient): Promise<ReleaseModeClient> => {
    return new Promise((resolve) => {
      navigate(generatePath('/sportello/modalita-rilascio/modifica/:id', { id: cell.code || '' }));
      resolve(cell);
    });
  });

  const onTrashPress = useEventCallback((cell: ReleaseModeClient): Promise<ReleaseModeClient> => {
    return new Promise((resolve) => {
      setDeleteAlertModal({ open: true, element: cell });
      resolve(cell);
    }) as Promise<ReleaseModeClient>;
  });

  const handleChangePage = useEventCallback((newPage: number) => {
    filterForm.setFieldValue('page', newPage);
  });

  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    const newDataset = { ...filterForm.values, rowsPerPage: rowsPerPage || 10, page: 0 };
    filterForm.setValues(newDataset);
  });

  const onSort = useEventCallback((orders: Order) => {
    const newDataset = { ...filterForm.values, orders };
    const queryString = transformDataset(newDataset);
    (
      getReleaseModeList({
        params: {},
        paramsSerializer: () => queryString,
      }) as unknown as ResponsePromise<void, void>
    ).then(() => {
      filterForm.setFieldValue('orders', orders);
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={ReleaseModeCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuova modalità rilascio"
        addBtnLabel="Aggiungi nuova modalità rilascio"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sportello/modalita-rilascio/nuovo');
        }}
        title="Gestione modalità rilascio"
      />
      <div className="pmi-my-3">
        <FiltersReleaseMode filtersForm={filterForm} />
      </div>
      {releaseModeList.data ? (
        ((releaseModeList.data as ReleaseModeResponse)?.list as Array<ReleaseModeClient>)
          ?.length ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterForm.values.orders}
            activeActions={auths}
            page={filterForm.values.page}
            rowsPerPage={filterForm.values.rowsPerPage}
            body={
              ((releaseModeList.data as ReleaseModeResponse)?.list as Array<ReleaseModeClient>) ||
              []
            }
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={(releaseModeList.data as ReleaseModeResponse)?.count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Modalità rilascio"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Modalità rilascio"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteFeatureAuth({
            url: `${__CERTIFICATION_API_URL__}/certificazione/modalita-rilascio/${
              deleteAlertModal.element?.code || ''
            }`,
          });
          setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          setDeleteAlertModal({ open: false, element: null });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Modalità rilascio"
        message={`La cancellazione della modalità ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterForm.values, page: 0 });
          getReleaseModeList({
            params: {},
            paramsSerializer: () => queryString,
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterForm.values, page: 0 });
          getReleaseModeList({
            params: {},
            paramsSerializer: () => queryString,
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </Fragment>
  );
};

ReleaseModeList.displayName = 'Release Mode List component';

export default ReleaseModeList;
